// Entity core (MOB-1). PURE (ENG-1).
//
// A minimal, deterministic living-entity system ticked in §5.5 phase 7 (entities)
// in stable id order. The sim knows only AABBs and behaviour; the box-rig
// *description* lives in the mob registry and the client builds Three meshes from
// it (MOB-1). Behaviour is composed from archetypes (grazer/melee/ranged, MOB-2)
// plus per-name overrides (creeper/spider/zombie, M5.2). Projectiles (MOB-5) and
// XP orbs (PLR-5) are lightweight sibling entities owned by the same system.
import { MOB, PROJECTILE, XP_ORB, FLYING, SWIM, FIREBALL, FIREWORK, TAME, SLIME, SCR, HER } from '../config';
import { rollHorseAttributes } from '../game/horse';
import type { Box } from '../shapes';
import { collideAndMove, type Body } from '../engine/physics';
import {
  decodeEntityName, decodeScriptMem, decodeScriptOverride, encodeScriptMem, isScriptMemKey, isScriptMemValue,
  type ScriptMem, type ScriptMemValue,
} from '../engine/saves';
import { SALT_HER_ROLL, type Rng } from '../engine/rng';
import { heredityOn, pace as paceOf, type Pace, type RuleSource } from '../heredity/rule';
import { oldAgeCheck } from '../heredity/aging';
import { decodeGenome, encodeGenome, rollGenome, IDENTITY, type Traits } from '../heredity/genome';
import { express, woolVariant } from '../heredity/express';
import type { MobDef } from '../registry/schemas';
import { cloneStack, type ItemStack } from '../game/inventory';
import { clonePack, type MountPack } from '../game/containers/mount_storage';
import { behaviorFor, deathFor, dispatchMobHook, scriptNameOf, type MobScripts } from './behaviors';
import type { MobHook } from '../script/system';
import { explodeAt, type Explodable } from './explosion';
import { splashBurst } from '../game/potion';
import { isBeneficialEffect as isBeneficial } from '../game/effects';

export type MobCategory = MobDef['category'];
export type Difficulty = 'peaceful' | 'easy' | 'normal' | 'hard';

/** Just enough world for entity physics + AI probing (ENG-1 keeps it an interface). */
export interface MobWorld extends Explodable {
  isSolid(x: number, y: number, z: number): boolean;
  collisionBoxesAt(x: number, y: number, z: number): readonly Box[];
  getBlockLight(x: number, y: number, z: number): number;
  getSkyLight(x: number, y: number, z: number): number;
  isFluid(x: number, y: number, z: number): boolean;
  /** Grazable surface (grass_block / tall_grass) — sheep regrow wool on it (M13.1). */
  isGrass?(x: number, y: number, z: number): boolean;
  /** Whether a block state is a whitelisted carry block (grass/dirt/sand…, M13.2). */
  isCarryable?(state: number): boolean;
  /** Place a block state at a cell (enderman sets a carried block down, M13.2). */
  placeBlock?(x: number, y: number, z: number, state: number): void;
}

/** The player as the entity system sees it (solo: one; server: many). */
export interface PlayerHandle {
  x: number; y: number; z: number;
  /** Eye height above feet (for a mob/skeleton to aim at). */
  eyeY: number;
  invulnerable: boolean;
  /** Take a melee/projectile hit + horizontal knockback (PLR-3). */
  hurt(amount: number, kbX: number, kbZ: number): void;
  /** Absorb an XP orb (PLR-5). */
  addXp(amount: number): void;
  /** Set on fire for `ticks` (ghast fireball, M7.2). Optional (host-provided). */
  setFire?(ticks: number): void;
  /** Inflict a status effect (husk → hunger, stray → slowness, M13.2). Optional. */
  addEffect?(id: string, amplifier: number, ticks: number): void;
  /** Restore health (a beneficial/instant_health splash potion, MB.4). Optional. */
  heal?(amount: number): void;
  /** Unit look direction (for the enderman stare test, M13.2). Optional. */
  lookDir?(): { x: number; y: number; z: number };
  /** Breeding: is this player currently offering `item` (held in survival)? */
  offering(item: string): boolean;
  /** Consume one of the offered breed item. */
  consumeOffered(item: string): void;
}

/** Per-tick inputs the whole system reads (players, time, difficulty, sinks). */
export interface MobFrame {
  world: MobWorld;
  dayTime: number;
  difficulty: Difficulty;
  mobGriefing: boolean;
  /** Raining in the overworld — a snow golem melts in it (M13.1). Defaults false. */
  raining?: boolean;
  /** The sim tick this frame belongs to (ARCH §27.8/§27.19) — `World.tickCount`, the
   *  one monotonic counter `engine/tick.ts` advances once per `runTick`. It reaches
   *  the mob-script schedule from here because that is where a per-tick input belongs,
   *  and because the entity layer sees a `MobWorld`, which has no clock of its own.
   *
   *  **Optional, and its absence is a single honest condition:** with no tick there is
   *  no cadence stagger, no frame ceiling (a ceiling with no frame boundary would never
   *  reset) and no per-dispatch rng fork — see `script/system.ts`'s header. Nothing
   *  else in this file reads it, so an unscripted world is untouched by it. */
  tick?: number;
  players: readonly PlayerHandle[];
  /** Loot from a killed mob becomes a loose item entity (E-17). */
  dropLoot(stack: ItemStack, x: number, y: number, z: number): void;
}

export interface Entity extends Body {
  id: number;
  type: string;
  category: MobCategory;
  yaw: number;
  pitch: number;
  health: number;
  maxHealth: number;
  /** Ticks alive. */
  age: number;
  /** Remaining burning ticks (daylight burn / lava). */
  fire: number;
  baby: boolean;
  /** Baby → adult growth countdown (ticks); 0 for adults. */
  growTimer: number;
  // ── AI scratch (set by archetypes each tick) ──
  /** Desired horizontal velocity this tick (world space), applied by physics. */
  wishX: number;
  wishZ: number;
  /** Desired vertical velocity for flying mobs (ghast); ignored for grounded. */
  wishY: number;
  wishJump: boolean;
  /** Flies in 3D, ignoring gravity (MOB-2 flying: ghast). */
  flying: boolean;
  /** Swims in 3D but stays inside fluid, and falls if beached (squid, fish,
   *  dolphin, guardian). Mutually exclusive with `flying`. */
  aquatic: boolean;
  /** Steers around water while wandering/chasing (a land animal). False for
   *  swimmers and for the amphibious mobs that belong in it (drowned, turtle). */
  avoidsWater: boolean;
  /** Target player index (−1 = none) for melee/ranged. */
  targetIdx: number;
  /** Target entity id (−1 = none) for mob-vs-mob combat (golem/assisting wolf). */
  targetEntityId: number;
  attackCooldown: number;
  wanderTimer: number;
  wanderAngle: number;
  /** Breeding (MOB-2): in-love ticks and post-breed cooldown. */
  loveTimer: number;
  breedCooldown: number;
  /** Recent-damage flash timer (client renders red). */
  hurtTimer: number;
  /** Ticks with no player within DESPAWN.SOFT (drives random despawn). */
  idleTimer: number;
  /** Named/tamed/bred-by-player → never despawns (MOB-4). */
  persistent: boolean;
  removed: boolean;
  /** Creeper swell / generic fuse (MOB special). */
  fuse: number;
  // ── Durable mob state (MOB-2, M13.1; persisted in SavedEntity) ──
  /** Tamed by a player (wolf/cat) — follows the owner and never despawns. */
  tamed: boolean;
  /** A tamed pet ordered to sit/stay (toggled on interact). */
  sitting: boolean;
  /** Neutral-mob anger countdown (ticks); >0 = hostile after a provoke. */
  angerTimer: number;
  /** Render/behaviour variant index (sheep colour, …). */
  variant: number;
  /** Sheared (sheep) — no wool until it regrows by eating grass. */
  sheared: boolean;
  /** Free scratch for overrides. */
  data: Record<string, number>;
  /** A chested pack animal's saddlebags (M19 remainder). `data` is numbers only,
   *  and a container has to live somewhere the animal carries with it — so it hangs
   *  off the entity rather than a side map keyed by id, which would not survive a
   *  save/load or an entity being culled and respawned. */
  pack?: MountPack;
  /** Per-entity script memory (§27.7, SCR-8) — a bag of its OWN, never `data`, which
   *  is engine scratch the archetypes and the renderer read. Created lazily on the
   *  first `mem.set`, so a mob that never ran a script saves byte-identically to
   *  pre-M45 (the §27.19 parity claim). It saves with the entity (ENG-11 v3) and is
   *  NEVER replicated — no client needs it to render a pose. Read and written only
   *  through `scriptMemGet`/`scriptMemSet` below, which own every §27.7 cap. */
  scriptMem?: ScriptMem;
  /** Per-instance script (§27.13, SCR-18): the script NAME this one entity runs INSTEAD
   *  of its type's `MobDef.script` — "this one sheep is the boss". Set by the editor's
   *  "Attach to this entity" or by `summon(mob, x, y, z, scriptId)`. Absent ⇒ the type's
   *  script, exactly as before. Saves with the entity (v3) and never replicates, like
   *  `scriptMem`. A name that no longer resolves falls back to the type's script with a
   *  logged warning (E-50) — the entity never breaks. */
  scriptOverride?: string;
  /** Transient, never saved: the dangling override already warned about, so E-50's
   *  warning is logged once per entity and name rather than once per dispatch. */
  scriptOverrideWarned?: string;
  // ── Heredity (HER-2, ARCH §28.4) ──
  /** This animal's genome, laid out by its species' gene table. Absent for a species
   *  with no table, and for any animal that has never lived under the rule — so an
   *  entity in a rule-off world carries no new state at all. */
  genome?: Uint8Array;
  /** The scalars the genome expresses (§28.6), computed ONCE at spawn/load/toggle and
   *  cached (D-TF-120). `IDENTITY` with the rule off or no table. */
  traits: Traits;
  /** Transient, never saved: a genetic grazer's remaining flee ticks (§28.7 — duration
   *  × `fleeMul`, and the startle a skittish neighbour catches). Absent outside the rule. */
  fleeTimer?: number;
  /** Parent ids for lineage (§28.5); absent = none (§28.4's `[0,0]`). */
  parents?: [number, number];
  /** A name-tag name (HER-10, ARCH §28.9). Any species, any rule state; a named animal is
   *  persistent and — under the rule — exempt from old age. Absent = unnamed. */
  name?: string;
}

/** A flying arrow (MOB-5). Shares the ballistic integrator in projectiles/. */
export interface Projectile {
  id: number;
  type: string;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  age: number;
  /** id of the entity that fired it (never self-hit). */
  ownerId: number;
  damage: number;
  stuck: boolean;
  removed: boolean;
  /** Status effect applied to the target on hit (stray tipped arrow, M13.2). */
  effect?: { id: string; amplifier: number; ticks: number };
}

/** A collectable experience orb (PLR-5). */
export interface XpOrb {
  id: number;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  value: number;
  age: number;
  pickupDelay: number;
}

/** What archetypes and overrides get — the world, rng, frame, and system verbs. */
export interface MobContext {
  world: MobWorld;
  rng: Rng;
  frame: MobFrame;
  sys: EntitySystem;
}

/** Ticks a hit mob stays "hurt" (the red flash, and a grazer's flee). Named at M49.3
 *  because heredity's flee duration is a multiple of it; the value is unchanged. */
export const HURT_TICKS = 10;

/** Mobs that roll per-animal speed/jump/health attributes on spawn (M19). */
const HORSE_KINDS = new Set(['horse', 'donkey', 'mule']);

/** Render/collision scale: babies are half-size; a sized mob (slime) scales by
 *  its `variant` (size 1/2/4). Everything else is 1× (MOB-2). */
const scaleOf = (e: Entity, def: MobDef): number =>
  // × the genome's size (§28.6) — `IDENTITY.scale` is exactly 1, so rule off is x·1 = x.
  (e.baby ? MOB.BABY_SCALE : def.sized ? Math.max(1, e.variant) : 1) * e.traits.scale;
const halfOf = (e: Entity, def: MobDef): number => (def.width * scaleOf(e, def)) / 2;
const heightOf = (e: Entity, def: MobDef): number => def.height * scaleOf(e, def);

/** How a mob moves (MOB-3). `movement` in the mob JSON wins; otherwise water
 *  categories/submerged spawners swim, the flying + ambient archetypes fly, and
 *  everything else walks. The inference exists because `ambient` is an AI style
 *  rather than a medium: a bat, a squid and a turtle all ship on it. */
const movementOf = (def: MobDef): 'ground' | 'amphibious' | 'air' | 'water' => {
  if (def.movement) return def.movement;
  if (def.category === 'water_creature' || def.category === 'water_ambient' || def.spawn.aquatic) return 'water';
  if (def.archetype === 'flying' || def.archetype === 'ambient') return 'air';
  return 'ground';
};

export class EntitySystem {
  readonly entities: Entity[] = [];
  readonly projectiles: Projectile[] = [];
  readonly orbs: XpOrb[] = [];
  private nextId = 1;
  /** Owner-assist: the last mob a player struck, and how long it stays a valid
   *  assist target (TAME.ASSIST_TICKS). A tamed wolf attacks this entity (MOB-2). */
  assistTargetId = -1;
  private assistTicks = 0;

  constructor(
    readonly defs: ReadonlyMap<string, MobDef>,
    readonly rng: Rng,
  ) {}

  get count(): number { return this.entities.length; }

  def(type: string): MobDef | undefined { return this.defs.get(type); }

  /** Count live entities of a spawn category (MOB-4 cap accounting). */
  countCategory(cat: MobCategory): number {
    let n = 0;
    for (const e of this.entities) if (!e.removed && e.category === cat) n++;
    return n;
  }

  /** Spawn a mob at (x,y,z). Returns the entity, or null for an unknown type. */
  spawn(type: string, x: number, y: number, z: number, opts: {
    baby?: boolean; persistent?: boolean; variant?: number; tamed?: boolean; scriptOverride?: string;
    /** §28.4/§28.5: a genome to carry (a recombined child, a loaded animal) instead of a
     *  fresh roll; and its parents. Both ignored for a species with no table. */
    genome?: Uint8Array; parents?: [number, number];
    /** A load restoring a SAVED id (M48.6): lineage and notebook records refer to
     *  animals by id, so an animal with heredity state keeps its id across a reload.
     *  `loadJSON` reserves every such id before any fresh one is handed out. */
    id?: number;
  } = {}): Entity | null {
    const def = this.defs.get(type);
    if (!def) return null;
    const baby = opts.baby ?? false;
    const e: Entity = {
      id: opts.id ?? this.nextId++,
      type,
      category: def.category,
      x, y, z, vx: 0, vy: 0, vz: 0, onGround: false,
      yaw: this.rng.nextFloat() * Math.PI * 2,
      pitch: 0,
      health: def.maxHealth,
      maxHealth: def.maxHealth,
      age: 0,
      fire: 0,
      baby,
      growTimer: 0,
      wishX: 0, wishZ: 0, wishY: 0, wishJump: false,
      // Water mobs move in 3D like fliers but are confined to fluid; fliers
      // ignore gravity entirely (ghast, bat). See movementOf for the inference.
      aquatic: movementOf(def) === 'water',
      flying: movementOf(def) === 'air',
      avoidsWater: movementOf(def) === 'ground',
      targetIdx: -1,
      targetEntityId: -1,
      attackCooldown: 0,
      wanderTimer: 0,
      wanderAngle: 0,
      loveTimer: 0,
      breedCooldown: baby ? 0 : 0,
      hurtTimer: 0,
      idleTimer: 0,
      persistent: (opts.persistent ?? false) || (opts.tamed ?? false),
      removed: false,
      fuse: 0,
      tamed: opts.tamed ?? false,
      sitting: false,
      angerTimer: 0,
      // A mob declaring `variants` rolls a cosmetic livery when none was asked for
      // (tropical fish). Every other mob keeps variant 0 exactly as before.
      variant: opts.variant ?? (def.variants && def.variants > 1 ? this.rng.nextInt(def.variants) : 0),
      sheared: false,
      data: {},
      traits: IDENTITY,
      // §27.13: attached at spawn (`summon(mob, x, y, z, scriptId)`). Only set when
      // asked for, so an ordinary spawn builds the same object it always did.
      ...(opts.scriptOverride !== undefined ? { scriptOverride: opts.scriptOverride } : {}),
    };
    // Every horse is its own animal (M19): speed, jump and health are rolled from
    // three draws apiece, so most are average and a few are worth hunting for. The
    // multipliers ride in `data` where the mount code and the renderer can see them.
    // §28.4: under the rule, a species with a table gets a genome — the caller's (a
    // child, a load) or a fresh roll from `her:roll` keyed by id, which DERIVES from the
    // system's seed and draws nothing from `this.rng`. Off, nothing here runs at all.
    const genetic = def.genes !== undefined && this.heredityIsOn();
    if (genetic) {
      e.genome = opts.genome ?? rollGenome(def.genes!, this.rng.derive(SALT_HER_ROLL, e.id));
      if (opts.parents) e.parents = [opts.parents[0], opts.parents[1]];
      // §28.6: a sheep is born in its natural colour — the wool gene's expressed allele —
      // unless the caller already knows its colour (a load restores a dyed sheep).
      if (opts.variant === undefined) {
        const natural = woolVariant(def.genes, e.genome);
        if (natural !== undefined) e.variant = natural;
      }
    }
    // The horse fold-in (§28.4, D-TF-113): under the rule a horse-family animal takes
    // speed/jump/health from its genome (`applyTraits` below) and `rollHorseAttributes` is
    // NOT called; off, this branch is exactly what it was — the M48.2 golden pins it.
    if (HORSE_KINDS.has(def.name) && !genetic) {
      const attrs = rollHorseAttributes(() => this.rng.nextFloat());
      e.data.speedMul = attrs.speedMul;
      e.data.jumpMul = attrs.jumpMul;
      e.maxHealth = def.maxHealth * attrs.healthMul;
      e.health = e.maxHealth;
    }
    if (genetic) this.applyTraits(e, def, true);
    // Sized mobs (slimes) carry their size in `variant`; health scales as size²
    // (MC: 1/4/16 HP) and the AABB scales via scaleOf (MOB-2, M13.2).
    if (def.sized) {
      const size = opts.variant ?? SLIME.MAX_SIZE;
      e.variant = size;
      e.maxHealth = def.maxHealth * size * size;
      e.health = e.maxHealth;
    }
    this.entities.push(e);
    return e;
  }

  /** Nearest non-invulnerable player to (x,y,z) within `range`, or null. Used for
   *  AI targeting — an invulnerable (creative/dead) player is not a valid target. */
  nearestPlayer(frame: MobFrame, x: number, y: number, z: number, range: number): { idx: number; dist: number } | null {
    let bestIdx = -1;
    let bestD2 = range * range;
    for (let i = 0; i < frame.players.length; i++) {
      const p = frame.players[i]!;
      if (p.invulnerable) continue;
      const d2 = (p.x - x) ** 2 + (p.y - y) ** 2 + (p.z - z) ** 2;
      if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
    }
    return bestIdx < 0 ? null : { idx: bestIdx, dist: Math.sqrt(bestD2) };
  }

  /** Nearest player of ANY kind (invulnerable included) within `range`. Pets
   *  follow their owner even when the owner is in creative (MOB-2 tamable). */
  nearestAnyPlayer(frame: MobFrame, x: number, y: number, z: number, range: number): { idx: number; dist: number } | null {
    let bestIdx = -1;
    let bestD2 = range * range;
    for (let i = 0; i < frame.players.length; i++) {
      const p = frame.players[i]!;
      const d2 = (p.x - x) ** 2 + (p.y - y) ** 2 + (p.z - z) ** 2;
      if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
    }
    return bestIdx < 0 ? null : { idx: bestIdx, dist: Math.sqrt(bestD2) };
  }

  /** Distance to the nearest player of ANY kind (invulnerable included). Despawn
   *  is about player *presence* (loaded/active area), not target validity — a
   *  creative or dead player must still keep nearby mobs alive (MOB-4). */
  private nearestPlayerDist(frame: MobFrame, x: number, y: number, z: number): number {
    let best = Infinity;
    for (const p of frame.players) {
      const d2 = (p.x - x) ** 2 + (p.y - y) ** 2 + (p.z - z) ** 2;
      if (d2 < best) best = d2;
    }
    return Math.sqrt(best);
  }

  /** Nearest entity a ray (unit dir) hits within `maxDist` — for player melee
   *  (ARCH §5.4: entity hit test vs AABBs). Returns the entity + hit distance. */
  hitscan(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): { entity: Entity; dist: number } | null {
    let best: Entity | null = null;
    let bestT = maxDist;
    for (const e of this.entities) {
      if (e.removed) continue;
      const def = this.defs.get(e.type)!;
      const h = halfOf(e, def), hgt = heightOf(e, def);
      const t = rayAabb(ox, oy, oz, dx, dy, dz, e.x - h, e.y, e.z - h, e.x + h, e.y + hgt, e.z + h);
      if (t !== null && t < bestT) { bestT = t; best = e; }
    }
    return best ? { entity: best, dist: bestT } : null;
  }

  /** A live entity by id (targeting), or null if gone/removed. */
  byId(id: number): Entity | null {
    if (id < 0) return null;
    for (const e of this.entities) if (e.id === id && !e.removed) return e;
    return null;
  }

  /** Nearest live entity to (x,y,z) within `range` matching `pred` (excludes self). */
  nearestEntity(self: Entity, range: number, pred: (e: Entity) => boolean): Entity | null {
    let best: Entity | null = null;
    let bestD2 = range * range;
    for (const e of this.entities) {
      if (e.removed || e.id === self.id || !pred(e)) continue;
      const d2 = (e.x - self.x) ** 2 + (e.y - self.y) ** 2 + (e.z - self.z) ** 2;
      if (d2 < bestD2) { bestD2 = d2; best = e; }
    }
    return best;
  }

  /** Live entities within `radius` of (x,y,z), optionally filtered by type. */
  nearby(x: number, y: number, z: number, radius: number, type?: string): Entity[] {
    const r2 = radius * radius;
    const out: Entity[] = [];
    for (const e of this.entities) {
      if (e.removed) continue;
      if (type && e.type !== type) continue;
      if ((e.x - x) ** 2 + (e.y - y) ** 2 + (e.z - z) ** 2 <= r2) out.push(e);
    }
    return out;
  }

  /** First live entity whose AABB contains the projectile point (excludes the
   *  shooter), or null. Lets an in-flight arrow/snowball strike a mob (MOB-5). */
  private projectileEntityHit(p: Projectile, x: number, y: number, z: number): Entity | null {
    for (const e of this.entities) {
      if (e.removed || e.id === p.ownerId) continue;
      const def = this.defs.get(e.type)!;
      const h = halfOf(e, def), hgt = heightOf(e, def);
      if (x > e.x - h && x < e.x + h && y > e.y && y < e.y + hgt && z > e.z - h && z < e.z + h) return e;
    }
    return null;
  }

  /** Fire a projectile (MOB-5). Owner never self-hits. */
  spawnProjectile(type: string, x: number, y: number, z: number, vx: number, vy: number, vz: number, ownerId: number, damage: number, effect?: Projectile['effect']): Projectile {
    const p: Projectile = { id: this.nextId++, type, x, y, z, vx, vy, vz, age: 0, ownerId, damage, stuck: false, removed: false, effect };
    this.projectiles.push(p);
    return p;
  }

  /** Scatter `total` XP as one or more orbs (PLR-5 death reward). */
  spawnXp(total: number, x: number, y: number, z: number): void {
    let left = Math.floor(total);
    while (left > 0) {
      // Vanilla splits XP into orbs of decreasing denomination; keep it simple.
      const v = left >= 17 ? 17 : left >= 7 ? 7 : left >= 3 ? 3 : left >= 1 ? 1 : left;
      left -= v;
      this.orbs.push({
        id: this.nextId++,
        x, y: y + 0.2, z,
        vx: (this.rng.nextFloat() - 0.5) * 0.2,
        vy: 0.1 + this.rng.nextFloat() * 0.1,
        vz: (this.rng.nextFloat() - 0.5) * 0.2,
        value: v,
        age: 0,
        pickupDelay: 10,
      });
    }
  }

  /** Explode at a point (creeper/RED-8). Damages nearby entities/players + breaks blocks. */
  explode(frame: MobFrame, x: number, y: number, z: number, power: number): void {
    explodeAt(frame.world, x, y, z, power, frame.mobGriefing);
    // Entity + player damage falls off linearly to the blast radius (RED-8).
    const radius = power * 2;
    for (const e of this.entities) {
      if (e.removed) continue;
      const d = Math.hypot(e.x - x, e.y + 0.5 - y, e.z - z);
      if (d > radius) continue;
      const dmg = (1 - d / radius) * power * 7;
      this.damage(e, dmg, frame, (e.x - x) * 0.4, (e.z - z) * 0.4);
    }
    for (const p of frame.players) {
      const d = Math.hypot(p.x - x, p.y + 0.5 - y, p.z - z);
      if (d > radius) continue;
      const dmg = (1 - d / radius) * power * 7;
      p.hurt(dmg, (p.x - x) / (d || 1) * 0.8, (p.z - z) / (d || 1) * 0.8);
    }
  }

  /** Apply damage to a mob (knockback + hurt flash); rolls loot + XP on death.
   *  A player hit arms two reactions: neutral mobs turn angry, and tamed pets are
   *  told to assist against this attacker (MOB-2). */
  damage(e: Entity, rawAmount: number, frame: MobFrame, kbX = 0, kbZ = 0, byPlayer = false, knockV = 0.36): void {
    if (e.removed || rawAmount <= 0) return;
    if (this.invulnerable?.(e)) return; // charging-up wither shrugs off all damage (M20)
    // Worn protection softens the hit before anything else sees it (M61 horse
    // barding). The host owns the curve; absent, the raw amount stands.
    const amount = this.mitigateDamage?.(e, rawAmount) ?? rawAmount;
    if (amount <= 0) return;
    const dealt = Math.min(amount, Math.max(0, e.health)); // real damage, not overkill / the creative one-shot sentinel
    e.health -= amount;
    e.hurtTimer = HURT_TICKS;
    e.loveTimer = 0;
    if (byPlayer) {
      this.onPlayerDamage?.(e, dealt); // M24 Damage Dealt stat (host bumps); death voice/loot handled below
      const def = this.defs.get(e.type);
      if (def?.neutral) e.angerTimer = TAME.ANGER_TICKS; // a struck neutral retaliates
      // Tamed pets defend their owner: assist against whatever the owner attacked.
      this.assistTargetId = e.id;
      this.assistTicks = TAME.ASSIST_TICKS;
    }
    const kb = Math.hypot(kbX, kbZ);
    if (kb > 0) {
      e.vx += (kbX / kb) * MOB.MELEE.KNOCKBACK;
      e.vz += (kbZ / kb) * MOB.MELEE.KNOCKBACK;
      e.vy = Math.max(e.vy, knockV);
      e.onGround = false;
    }
    // §27.6's `on hurt` OBSERVES: it fires after the damage has already landed, and
    // its answer is discarded — `handled()` here cancels nothing, because "no
    // invulnerable mobs" is a rule about what a script may not do. Placing the hook
    // after the health write is what makes that structural instead of a promise.
    this.mobHook(e, frame, 'hurt');
    if (e.health <= 0) this.kill(e, frame, byPlayer);
    else this.onMobHurt?.(e); // survived → hurt voice (M23 AUD-2); death voice is onMobDeath
  }

  private kill(e: Entity, frame: MobFrame, byPlayer: boolean): void {
    if (e.removed || this.dying.has(e.id)) return;
    // §27.6's `on death`: ONCE, and BEFORE removal — before removal because a script
    // asking `self.pos` in its own death handler should still find the mob alive and in
    // the world, and once because of the `dying` latch above: the handler may call
    // `self.hurt`, and without the latch that re-entry would reach this method a second
    // time with `removed` still false and roll the mob's loot TWICE. Like `on hurt` it
    // observes — the answer is discarded, so there is no totem by script and every line
    // below runs whatever the handler did.
    this.dying.add(e.id);
    this.mobHook(e, frame, 'death');
    this.dying.delete(e.id);
    e.removed = true;
    // Per-name death hook (slime split) runs even for babies/mid-air kills.
    deathFor(e.type)?.(e, this, frame);
    if (byPlayer) this.onPlayerKill?.(e, frame);
    const def = this.defs.get(e.type);
    if (!def || e.baby) return; // babies drop nothing (MC)
    this.rollLoot(def, frame, e.x, e.y, e.z);
    // A nearby sculk_catalyst absorbs the death's XP and blooms (M22); when it
    // does, no orbs drop (the catalyst ate them). Fires on any death; the host
    // decides whether a catalyst was in range.
    const absorbed = this.onMobDeath?.(e, def, frame) ?? false;
    if (byPlayer && def.xp > 0 && !absorbed) this.spawnXp(def.xp, e.x, e.y + 0.3, e.z);
  }

  private rollLoot(def: MobDef, frame: MobFrame, x: number, y: number, z: number): void {
    if (!def.drops) return;
    for (const stack of this.lootFn(def.drops)) frame.dropLoot(stack, x, y + 0.3, z);
  }

  /** Loot roller injected by the host (registry-backed). Default: no drops. */
  lootFn: (table: string) => ItemStack[] = () => [];

  /** Notified when a player kills a mob (RAID: a slain raid captain grants Bad
   *  Omen). The host wires the effect; the sim stays generic. Default: no-op. */
  onPlayerKill?: (e: Entity, frame: MobFrame) => void;

  /** Notified on any mob death (M22 sculk catalyst). Returns true if the death's
   *  XP was absorbed by a nearby catalyst — the sim then drops no orbs. The host
   *  owns the block lookup; default: nothing absorbs, orbs drop normally. */
  onMobDeath?: (e: Entity, def: MobDef, frame: MobFrame) => boolean;

  /** Notified when a mob is damaged but survives (M23 AUD-2 hurt voice). The host
   *  voices `entity.<type>.hurt`; the sim stays generic. Default: no-op. */
  onMobHurt?: (e: Entity) => void;

  /** Notified when a player deals damage to a mob (M24 Damage Dealt stat). The
   *  amount is the real damage landed, capped at the target's remaining health
   *  (never the creative one-shot sentinel). Default: no-op. */
  onPlayerDamage?: (e: Entity, amount: number) => void;

  /** Pre-damage gate: return true to make an entity immune to a damage instance
   *  (M20 wither is invulnerable while it charges up on spawn). Default: allow. */
  invulnerable?: (e: Entity) => boolean;

  /** Worn-protection curve: given an incoming amount, return what actually lands
   *  (M61 horse barding). Default: the hit stands unchanged. */
  mitigateDamage?: (e: Entity, amount: number) => number;

  /** Rider-control override (land-mount riding, M19): when it returns non-null for a
   *  mob, the rider's movement intent replaces its AI's wish this tick. */
  controlled?: (e: Entity) => { wishX: number; wishZ: number; jump: boolean } | null;

  // ── Mob scripting (ARCH §27.6, SCR-7) ───────────────────────────────────────

  /** The mob-script wiring (the runtime + the verb surface), injected by the host the
   *  same way `lootFn`/`onMobDeath`/`controlled` are. **Absent by default**, and with
   *  it absent not one line below behaves differently from pre-M45: every mob runs
   *  exactly its archetype (§27.19 parity). */
  mobScripts?: MobScripts;

  // ── Heredity (HER-1/2, ARCH §28.2/§28.4) ────────────────────────────────────

  /** Where the `heredity` / `heredityPace` gamerules are read from — the host's `World`.
   *  **Absent by default**, and absent reads as the rule off: an entity system nobody
   *  wired behaves exactly as it did before Part IX. */
  rules?: RuleSource;
  /** Where E-51's once-per-species warning goes (a host log). Optional. */
  warn?: (message: string) => void;
  /** §28.8's two events, for the host's chrome: an animal died of old age (a grey puff —
   *  no chat, no sound), or a feed was refused because the flock is at capacity (the
   *  action bar, for the player at index `player`). Optional; the sim needs neither. */
  onHeredity?: (event: HeredityEvent) => void;
  /** E-54: is this animal ridden or leashed? Only the host knows. An animal that is held
   *  when its lifespan runs out waits, and dies the first tick it is let go. */
  held?: (e: Entity) => boolean;
  /** The rule and pace read ONCE at the top of each tick, for the per-entity checks. */
  private tickRule = false;
  private tickPace: Pace = 1;
  /** §28.8's rate limit: the tick each player index last got a capacity message. */
  private readonly capMsgAt = new Map<number, number>();

  /** The world's heredity pace under the rule (§28.2), for archetypes. */
  heredityPace(): Pace {
    return this.rules ? paceOf(this.rules) : 1;
  }

  /** §28.8's carrying-capacity notice, rate-limited to one per player per
   *  `HER.CAP_MSG_TICKS`. The grazer calls this whenever it refuses a feed. */
  capacityRefused(playerIdx: number, tick: number): void {
    const last = this.capMsgAt.get(playerIdx);
    if (last !== undefined && tick - last < HER.CAP_MSG_TICKS) return;
    this.capMsgAt.set(playerIdx, tick);
    this.onHeredity?.({ kind: 'her:capacity', player: playerIdx });
  }

  /** §28.8: die of old age — a normal death's loot, NO XP orb, the `on death` hook, and
   *  the `her:oldAge` event (the notebook's `died`/`cause` hangs off it at M50.4). */
  private dieOfOldAge(e: Entity, def: MobDef, frame: MobFrame): void {
    if (e.removed || this.dying.has(e.id)) return;
    this.dying.add(e.id);
    this.mobHook(e, frame, 'death');
    this.dying.delete(e.id);
    e.removed = true;
    this.rollLoot(def, frame, e.x, e.y, e.z);
    this.onHeredity?.({ kind: 'her:oldAge', id: e.id, x: e.x, y: e.y, z: e.z });
  }
  /** The rule's value at the last tick — a flip is what triggers the re-express pass. */
  private ruleWasOn = false;
  /** Species already warned about under E-51 this session. */
  private readonly warnedGenomes = new Set<string>();

  /** Is heredity on for this world? (§28.2 — absent rules ⇒ off.) */
  heredityIsOn(): boolean {
    return this.rules !== undefined && heredityOn(this.rules);
  }

  /** Cache `e.traits` from its genome (§28.4) — and, for the horse family under the
   *  rule, write the expressed speed/jump/health where the mount code already reads them
   *  (`data.speedMul`, `data.jumpMul`, `maxHealth`), so that path needs no change. */
  private applyTraits(e: Entity, def: MobDef, on: boolean): void {
    e.traits = express(def.genes, e.genome, on);
    if (!on || !e.genome || !HORSE_KINDS.has(def.name)) return;
    e.data.speedMul = e.traits.speedMul;
    e.data.jumpMul = e.traits.jumpMul;
    const wasFull = e.health >= e.maxHealth;
    e.maxHealth = def.maxHealth * e.traits.healthMul;
    e.health = wasFull ? e.maxHealth : Math.min(e.health, e.maxHealth);
  }

  /** Replace an animal's genome (a debug/editor seam — `voxel.heredity.setGenome`) and
   *  re-express it under the world's current rule, exactly as a spawn would have. */
  setGenome(e: Entity, genome: Uint8Array): void {
    const def = this.defs.get(e.type);
    if (!def?.genes) return;
    e.genome = genome;
    this.applyTraits(e, def, this.heredityIsOn());
  }

  /** §28.2's live toggle. On a flip ON, every table species gets a genome (rolled from
   *  `her:roll` by id, so two worlds toggled at different ticks agree) and its traits; on
   *  a flip OFF, genomes stay and traits return to `IDENTITY`. Runs once per FLIP, never
   *  per tick (D-TF-120), and with the rule off and never flipped it reads one string and
   *  returns — no draw, no allocation. */
  private syncHeredity(): void {
    const on = this.heredityIsOn();
    if (on === this.ruleWasOn) return;
    this.ruleWasOn = on;
    for (const e of this.entities) {
      if (e.removed) continue;
      const def = this.defs.get(e.type);
      if (!def?.genes) continue;
      if (on && !e.genome) e.genome = rollGenome(def.genes, this.rng.derive(SALT_HER_ROLL, e.id));
      this.applyTraits(e, def, on);
    }
  }

  /** Depth guard for script-caused hooks. A script's `self.attack` damages another mob,
   *  whose `on hurt` could attack back, whose `on hurt`… — so while ONE hook is in
   *  flight no other hook dispatches, and the inner event simply falls through to its
   *  archetype. This is an E-30 backstop (the tick loop must survive any script), NOT
   *  §27.10's cascade budget: that one COUNTS what it clips and reports it to the
   *  author, and it lands with the rest of the interop limits in M46. */
  private hookDepth = 0;

  /** Entity ids whose `on death` hook is running. `kill` fires the hook BEFORE marking
   *  the mob removed (§27.6), so until that mark lands this latch is the only thing
   *  that makes a death idempotent — a handler calling `self.hurt` would otherwise
   *  re-enter `kill` and roll the loot table twice. */
  private readonly dying = new Set<number>();

  /** Run one §27.6 hook for an entity the caller has already resolved (the tick loop
   *  holds `def` and the `MobContext` already, so this path allocates nothing extra).
   *  Returns true iff the script called `handled()`, which suppresses the archetype /
   *  default for THIS dispatch only. Never throws (E-30). */
  private runMobHook(e: Entity, def: MobDef, ctx: MobContext, hook: MobHook): boolean {
    const scripts = this.mobScripts;
    if (!scripts || scriptNameOf(e, def) === undefined || this.hookDepth > 0) return false;
    this.hookDepth++;
    try {
      return dispatchMobHook(e, def, ctx, hook, scripts);
    } finally {
      // `dispatchMobHook` does not throw; the finally is here so that if a host verb
      // ever did, the guard could not latch shut and silence every later hook.
      this.hookDepth--;
    }
  }

  /** The same hook from a call site that holds only a frame (damage, death, interact).
   *  The unscripted path returns before building a `MobContext`, so an ordinary hit on
   *  an ordinary mob allocates nothing. */
  private mobHook(e: Entity, frame: MobFrame, hook: MobHook): boolean {
    const def = this.defs.get(e.type);
    if (!this.mobScripts || !def || scriptNameOf(e, def) === undefined) return false;
    return this.runMobHook(e, def, { world: frame.world, rng: this.rng, frame, sys: this }, hook);
  }

  /** A player right-clicked this mob (§27.6 `on interact`). The script hook runs FIRST
   *  and `fallback` — the host's default interaction (shear, breed, tame, trade, mount)
   *  — runs only when the script did not call `handled()`. Returns whether the
   *  interaction was consumed, which is what the host's `useEntity` reports.
   *
   *  §27.6 is explicit that this is the ONE hook `handled()` cancels something in: it
   *  "cancels the default interaction inside `on interact` and nothing else". */
  interact(e: Entity, frame: MobFrame, fallback: () => boolean): boolean {
    if (e.removed) return false;
    if (this.mobHook(e, frame, 'interact')) return true;
    return fallback();
  }

  /** Advance the whole system one game tick (§5.5 phase 7). */
  tick(frame: MobFrame): void {
    this.syncHeredity();
    this.tickRule = this.ruleWasOn;
    this.tickPace = this.tickRule ? this.heredityPace() : 1;
    const { world } = frame;
    // Owner-assist target expires after its window (or once the target is gone).
    if (this.assistTicks > 0 && --this.assistTicks === 0) this.assistTargetId = -1;
    if (this.assistTargetId >= 0 && !this.byId(this.assistTargetId)) this.assistTargetId = -1;
    // Mobs, in stable id order (the array is append-ordered by id).
    for (const e of this.entities) {
      if (e.removed) continue;
      e.age++;
      // §28.8 aging — only under the rule, only for an animal with a lifespan (a table
      // species' genome). Rule off: one boolean, and nothing else runs.
      if (this.tickRule && e.traits.lifespanTicks > 0) {
        const def0 = this.defs.get(e.type);
        if (def0 && oldAgeCheck(e, this.tickPace, this.held?.(e) ?? false) === null) {
          this.dieOfOldAge(e, def0, frame);
          continue;
        }
      }
      if (e.hurtTimer > 0) e.hurtTimer--;
      if (e.attackCooldown > 0) e.attackCooldown--;
      if (e.breedCooldown > 0) e.breedCooldown--;
      if (e.loveTimer > 0) e.loveTimer--;
      if (e.angerTimer > 0) e.angerTimer--;
      if (e.growTimer > 0 && --e.growTimer === 0) e.baby = false;

      const def = this.defs.get(e.type)!;
      const ctx: MobContext = { world, rng: this.rng, frame, sys: this };
      // Reset movement intent; the behaviour sets it.
      e.wishX = 0; e.wishZ = 0; e.wishY = 0; e.wishJump = false;
      // §27.4's tick placement and §27.6's fall-through, in two lines: the mob's
      // `on tick` hook runs BEFORE its archetype, and the archetype STILL RUNS
      // afterwards unless the script called `handled()` — a script adds behaviour, it
      // does not replace it. §27.8's cadence, frame ceiling and quarantine all live
      // inside `dispatchMob`, and they only ever make this return false, so the
      // archetype is what an off-beat, deferred or quarantined mob runs. This loop
      // walks `this.entities` in stable id order, which IS the order §27.8 requires the
      // frame ceiling to be consumed in — do not reorder it.
      const handled = this.runMobHook(e, def, ctx, 'tick');
      // A script that despawned or killed its own mob ends that entity's tick — there
      // is nothing left to steer, step, burn or despawn.
      if (e.removed) continue;
      if (!handled) behaviorFor(e.type, def.archetype)(e, def, ctx);
      const rider = this.controlled?.(e); // a ridden mount steers with its rider's input
      if (rider) { e.wishX = rider.wishX; e.wishZ = rider.wishZ; e.wishJump = rider.jump; }
      this.stepPhysics(e, def, world);
      this.environment(e, world, frame);

      if (!this.despawn(e, frame)) continue;
    }
    this.tickProjectiles(frame);
    this.tickOrbs(frame);
    // Compact removed entities (keeps id order).
    this.compact();
  }

  /** Apply AI movement intent + gravity + collision for one mob (MOB-3). */
  private stepPhysics(e: Entity, def: MobDef, world: MobWorld): void {
    const boxesAt = (x: number, y: number, z: number): readonly Box[] => world.collisionBoxesAt(x, y, z);
    const half = halfOf(e, def);
    const height = heightOf(e, def);
    if (e.aquatic) { this.stepSwimming(e, world, boxesAt, half, height); return; }
    if (e.flying) { this.stepFlying(e, boxesAt, half, height); return; }
    const inFluid = world.isFluid(Math.floor(e.x), Math.floor(e.y + 0.4), Math.floor(e.z));

    if (e.onGround) {
      e.vx = e.wishX;
      e.vz = e.wishZ;
      if (e.wishJump) { e.vy = MOB.JUMP_V; e.onGround = false; }
    } else {
      // Limited air steering.
      e.vx += e.wishX * 0.15;
      e.vz += e.wishZ * 0.15;
    }
    if (inFluid) {
      // Buoyancy keeps a swimming mob at the surface (avoids drowning forever).
      e.vy += 0.04;
      e.vx *= 0.9; e.vz *= 0.9;
      if (e.vy > 0.2) e.vy = 0.2;
    }
    if (e.wishX !== 0 || e.wishZ !== 0) e.yaw = Math.atan2(-e.wishX, -e.wishZ);

    collideAndMove(e, boxesAt, half, height);
    e.vy = (e.vy - MOB.GRAVITY) * MOB.DRAG;
    if (e.vy < -3) e.vy = -3;
  }

  /** Swimming for a water mob (MOB-3). Like flight, this honours `wishY` — ground
   *  physics ignores it, which is why a fish's dive/rise intent used to be thrown
   *  away and it just bobbed on buoyancy. Two things keep it in the sea:
   *  a velocity component is cancelled when the cell it would enter isn't fluid
   *  (so a fish can't swim out of its pool sideways), and out of water it loses
   *  swim control and falls, so a beached one flops back down rather than flying. */
  private stepSwimming(
    e: Entity, world: MobWorld, boxesAt: (x: number, y: number, z: number) => readonly Box[], half: number, height: number,
  ): void {
    const cell = (x: number, y: number, z: number): boolean => world.isFluid(Math.floor(x), Math.floor(y), Math.floor(z));
    if (!cell(e.x, e.y + 0.4, e.z)) {
      // Beached / airborne: no swim control, just fall.
      e.vx *= SWIM.BEACHED_DRAG;
      e.vz *= SWIM.BEACHED_DRAG;
      collideAndMove(e, boxesAt, half, height);
      e.vy = (e.vy - MOB.GRAVITY) * MOB.DRAG;
      if (e.vy < -3) e.vy = -3;
      return;
    }
    e.vx += (e.wishX - e.vx) * SWIM.ACCEL;
    e.vy += (e.wishY - e.vy) * SWIM.ACCEL;
    e.vz += (e.wishZ - e.vz) * SWIM.ACCEL;
    // Refuse to leave the water: probe one body-radius ahead on each axis.
    if (e.vx !== 0 && !cell(e.x + Math.sign(e.vx) * (half + SWIM.EDGE_PROBE), e.y + 0.4, e.z)) e.vx = 0;
    if (e.vz !== 0 && !cell(e.x, e.y + 0.4, e.z + Math.sign(e.vz) * (half + SWIM.EDGE_PROBE))) e.vz = 0;
    if (e.vy > 0 && !cell(e.x, e.y + height + SWIM.EDGE_PROBE, e.z)) e.vy = 0;
    const sp = Math.hypot(e.vx, e.vy, e.vz);
    if (sp > SWIM.MAX_SPEED) { const k = SWIM.MAX_SPEED / sp; e.vx *= k; e.vy *= k; e.vz *= k; }
    if (e.wishX !== 0 || e.wishZ !== 0) e.yaw = Math.atan2(-e.wishX, -e.wishZ);
    collideAndMove(e, boxesAt, half, height);
  }

  /** Free 3D flight for a flying mob (ghast): velocity eases toward the desired
   *  (wishX,wishY,wishZ), clamped to MAX_SPEED, with block collision but no gravity. */
  private stepFlying(e: Entity, boxesAt: (x: number, y: number, z: number) => readonly Box[], half: number, height: number): void {
    e.vx += (e.wishX - e.vx) * 0.2;
    e.vy += (e.wishY - e.vy) * 0.2;
    e.vz += (e.wishZ - e.vz) * 0.2;
    const sp = Math.hypot(e.vx, e.vy, e.vz);
    if (sp > FLYING.MAX_SPEED) { const k = FLYING.MAX_SPEED / sp; e.vx *= k; e.vy *= k; e.vz *= k; }
    if (e.wishX !== 0 || e.wishZ !== 0) e.yaw = Math.atan2(-e.wishX, -e.wishZ);
    collideAndMove(e, boxesAt, half, height);
  }

  /** Fire ticks (extinguished by water) + void floor. Daylight burn is set by the
   *  zombie override; the damage-over-time is applied here for every burning mob. */
  private environment(e: Entity, world: MobWorld, frame: MobFrame): void {
    if (e.fire > 0) {
      if (world.isFluid(Math.floor(e.x), Math.floor(e.y + 0.4), Math.floor(e.z))) {
        e.fire = 0;
      } else {
        e.fire--;
        if (e.age % 20 === 0) this.damage(e, 1, frame);
      }
    }
    if (e.y < -80) { e.removed = true; } // fell out of the world
  }

  /** MOB-4 despawn: instant beyond HARD, random beyond SOFT after idle. Returns
   *  false if the entity was removed. */
  private despawn(e: Entity, frame: MobFrame): boolean {
    if (e.persistent || e.baby) { e.idleTimer = 0; return true; }
    const dist = this.nearestPlayerDist(frame, e.x, e.y, e.z);
    if (dist > MOB.DESPAWN.HARD) { e.removed = true; return false; }
    if (dist > MOB.DESPAWN.SOFT) {
      e.idleTimer++;
      if (e.idleTimer > MOB.DESPAWN.IDLE_TICKS && this.rng.nextInt(MOB.DESPAWN.RANDOM_DENOM) === 0) {
        e.removed = true; return false;
      }
    } else {
      e.idleTimer = 0;
    }
    return true;
  }

  private tickProjectiles(frame: MobFrame): void {
    const { world } = frame;
    for (const p of this.projectiles) {
      if (p.removed || p.stuck) { if (++p.age > PROJECTILE.ARROW_LIFETIME) p.removed = true; continue; }
      // Ghast fireball (M7.2): straight-line (no gravity), explodes + ignites on impact.
      if (p.type === 'fireball') { this.tickFireball(p, frame); continue; }
      // Witch splash potion (MB.6): arcs like an arrow but shatters (no stick) into
      // an AoE cloud on the first block/ground/player contact.
      if (p.type === 'potion') { this.tickSplashPotion(p, frame); continue; }
      // Firework rocket (MB.7): self-propelled ascent, no gravity, cosmetic pop.
      if (p.type === 'firework') { this.tickFirework(p, frame); continue; }
      p.age++;
      if (p.age > PROJECTILE.ARROW_LIFETIME) { p.removed = true; continue; }
      // Ballistic integrate.
      p.vy -= PROJECTILE.ARROW_GRAVITY;
      p.vx *= PROJECTILE.ARROW_DRAG; p.vy *= PROJECTILE.ARROW_DRAG; p.vz *= PROJECTILE.ARROW_DRAG;
      const nx = p.x + p.vx, ny = p.y + p.vy, nz = p.z + p.vz;
      // Block hit → stick.
      if (world.isSolid(Math.floor(nx), Math.floor(ny), Math.floor(nz))) { p.stuck = true; continue; }
      // Player hit.
      for (const pl of frame.players) {
        if (pl.invulnerable) continue;
        if (Math.abs(pl.x - nx) < 0.5 && ny > pl.y && ny < pl.y + 1.9 && Math.abs(pl.z - nz) < 0.5) {
          const kb = Math.hypot(p.vx, p.vz) || 1;
          pl.hurt(p.damage, (p.vx / kb) * 0.6, (p.vz / kb) * 0.6);
          if (p.effect) pl.addEffect?.(p.effect.id, p.effect.amplifier, p.effect.ticks); // stray tipped arrow
          p.removed = true;
          break;
        }
      }
      if (p.removed) continue;
      // Mob hit (snow-golem snowball defends; skips the shooter). A snowball still
      // removes itself on a miss-less pass, so it never lingers as a stray arrow.
      const target = this.projectileEntityHit(p, nx, ny, nz);
      if (target) {
        const kb = Math.hypot(p.vx, p.vz) || 1;
        this.damage(target, p.damage, frame, (p.vx / kb) * 0.6, (p.vz / kb) * 0.6);
        p.removed = true;
        continue;
      }
      p.x = nx; p.y = ny; p.z = nz;
    }
    for (let i = this.projectiles.length - 1; i >= 0; i--) if (this.projectiles[i]!.removed) this.projectiles.splice(i, 1);
  }

  /** Advance one ghast fireball: fly straight; on block/player impact explode
   *  (power 1) and set nearby players on fire (M7.2). */
  private tickFireball(p: Projectile, frame: MobFrame): void {
    const { world } = frame;
    if (++p.age > FIREBALL.LIFETIME) { p.removed = true; return; }
    const nx = p.x + p.vx, ny = p.y + p.vy, nz = p.z + p.vz;
    let hit = world.isSolid(Math.floor(nx), Math.floor(ny), Math.floor(nz));
    if (!hit) {
      for (const pl of frame.players) {
        if (pl.invulnerable) continue;
        if (Math.abs(pl.x - nx) < 0.6 && ny > pl.y - 0.5 && ny < pl.y + 2.2 && Math.abs(pl.z - nz) < 0.6) { hit = true; break; }
      }
    }
    if (hit) {
      this.explode(frame, nx, ny, nz, FIREBALL.POWER);
      for (const pl of frame.players) {
        if (!pl.invulnerable && Math.hypot(pl.x - nx, pl.y + 0.9 - ny, pl.z - nz) <= FIREBALL.POWER * 2.5) pl.setFire?.(FIREBALL.FIRE_TICKS);
      }
      p.removed = true;
      return;
    }
    p.x = nx; p.y = ny; p.z = nz;
  }

  /** Advance one firework rocket (MB.7): self-propelled along its heading (no
   *  gravity), then detonate — remove itself, the cosmetic burst — after its fuse
   *  or if it flies into a block. Damageless (a rocket with no star). */
  private tickFirework(p: Projectile, frame: MobFrame): void {
    const { world } = frame;
    if (++p.age >= FIREWORK.FUSE) { p.removed = true; return; } // detonate
    p.vx *= FIREWORK.DRAG; p.vy *= FIREWORK.DRAG; p.vz *= FIREWORK.DRAG;
    // Accelerate along the current heading (keeps a dispenser-launched rocket going).
    const sp = Math.hypot(p.vx, p.vy, p.vz) || 1;
    p.vx += (p.vx / sp) * FIREWORK.THRUST;
    p.vy += (p.vy / sp) * FIREWORK.THRUST;
    p.vz += (p.vz / sp) * FIREWORK.THRUST;
    const nx = p.x + p.vx, ny = p.y + p.vy, nz = p.z + p.vz;
    if (world.isSolid(Math.floor(nx), Math.floor(ny), Math.floor(nz))) { p.removed = true; return; } // pops on impact
    p.x = nx; p.y = ny; p.z = nz;
  }

  /** Advance one splash potion (MB.6): fly ballistically, then shatter into an AoE
   *  cloud on the first solid block / ground / player contact (or at lifetime). */
  private tickSplashPotion(p: Projectile, frame: MobFrame): void {
    const { world } = frame;
    if (++p.age > PROJECTILE.ARROW_LIFETIME) { this.shatterPotion(p, frame, p.x, p.y, p.z); return; }
    p.vy -= PROJECTILE.ARROW_GRAVITY;
    p.vx *= PROJECTILE.ARROW_DRAG; p.vy *= PROJECTILE.ARROW_DRAG; p.vz *= PROJECTILE.ARROW_DRAG;
    const nx = p.x + p.vx, ny = p.y + p.vy, nz = p.z + p.vz;
    let hit = world.isSolid(Math.floor(nx), Math.floor(ny), Math.floor(nz));
    if (!hit) {
      for (const pl of frame.players) {
        if (pl.invulnerable) continue;
        if (Math.abs(pl.x - nx) < 0.6 && ny > pl.y - 0.2 && ny < pl.y + 2.0 && Math.abs(pl.z - nz) < 0.6) { hit = true; break; }
      }
    }
    if (hit) { this.shatterPotion(p, frame, nx, ny, nz); return; }
    p.x = nx; p.y = ny; p.z = nz;
  }

  /** Break a splash potion at (x,y,z): every player within SPLASH_RADIUS takes the
   *  potion's effect, scaled by proximity (game/potion.splashBurst). Duration
   *  effects are applied via addEffect; instant_damage hurts and instant_health
   *  heals (through the host heal seam) by the scaled magnitude. A beneficial
   *  splash reaches even an invulnerable/creative player. */
  private shatterPotion(p: Projectile, frame: MobFrame, x: number, y: number, z: number): void {
    p.removed = true;
    const eff = p.effect;
    if (!eff) return;
    const targets = frame.players.map((pl, idx) => ({ idx, x: pl.x, y: pl.y + 0.9, z: pl.z }));
    for (const h of splashBurst(eff.id, eff.amplifier, eff.ticks, x, y, z, targets)) {
      const pl = frame.players[h.idx]!;
      const magnitude = (4 << h.amplifier) * h.potency / 2; // instant potion magnitude (game/effects.applyInstant)
      if (h.instant) {
        if (h.effect === 'instant_health') pl.heal?.(magnitude);
        else if (!pl.invulnerable && h.effect === 'instant_damage') pl.hurt(magnitude, 0, 0);
      } else if (!pl.invulnerable || isBeneficial(h.effect)) {
        pl.addEffect?.(h.effect, h.amplifier, h.ticks);
      }
    }
  }

  private tickOrbs(frame: MobFrame): void {
    const { world } = frame;
    for (let i = this.orbs.length - 1; i >= 0; i--) {
      const o = this.orbs[i]!;
      if (++o.age > XP_ORB.DESPAWN_TICKS) { this.orbs.splice(i, 1); continue; }
      if (o.pickupDelay > 0) o.pickupDelay--;
      o.vy -= XP_ORB.GRAVITY;
      o.vx *= XP_ORB.DRAG; o.vz *= XP_ORB.DRAG;
      const ny = o.y + o.vy;
      if (o.vy < 0 && world.isSolid(Math.floor(o.x), Math.floor(ny - 0.1), Math.floor(o.z))) {
        o.y = Math.floor(ny - 0.1) + 1.0; o.vy = 0;
      } else { o.y = ny; }
      o.x += o.vx; o.z += o.vz;

      // Magnet toward + absorb into the nearest player (PLR-5).
      const near = this.nearestPlayer(frame, o.x, o.y, o.z, XP_ORB.MAGNET_RADIUS);
      if (near) {
        const pl = frame.players[near.idx]!;
        if (o.pickupDelay === 0 && near.dist <= XP_ORB.PICKUP_RADIUS) {
          pl.addXp(o.value);
          this.orbs.splice(i, 1);
          continue;
        }
        const dx = pl.x - o.x, dy = pl.y + 0.8 - o.y, dz = pl.z - o.z;
        const d = Math.hypot(dx, dy, dz) || 1;
        o.vx += (dx / d) * XP_ORB.MAGNET_ACCEL;
        o.vy += (dy / d) * XP_ORB.MAGNET_ACCEL;
        o.vz += (dz / d) * XP_ORB.MAGNET_ACCEL;
      }
    }
    // Merge nearby orbs (keeps counts small).
    const r2 = XP_ORB.MERGE_RADIUS * XP_ORB.MERGE_RADIUS;
    for (let i = 0; i < this.orbs.length; i++) {
      for (let j = i + 1; j < this.orbs.length; j++) {
        const a = this.orbs[i]!, b = this.orbs[j]!;
        if ((a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2 <= r2) {
          a.value += b.value;
          this.orbs.splice(j, 1); j--;
        }
      }
    }
  }

  private compact(): void {
    for (let i = this.entities.length - 1; i >= 0; i--) if (this.entities[i]!.removed) this.entities.splice(i, 1);
  }

  clear(): void { this.entities.length = 0; this.projectiles.length = 0; this.orbs.length = 0; }

  // ── Persistence (ARCH §3.6). Persist persistent mobs always, plus everything
  //    within `radius` of (px,pz); distant hostiles are dropped (E-16 vs despawn).
  //    Projectiles/XP orbs are transient and not saved. ──
  toJSON(px = 0, pz = 0, radius = Infinity): SavedEntity[] {
    const out: SavedEntity[] = [];
    const r2 = radius * radius;
    for (const e of this.entities) {
      if (e.removed) continue;
      if (!e.persistent && (e.x - px) ** 2 + (e.z - pz) ** 2 > r2) continue;
      // §27.7 / ENG-11 v3: memory travels with the entity — and ONLY here. It is
      // absent for a mob that never stored anything, so an unscripted mob's row is
      // byte-identical to a pre-M45 save.
      const scriptMem = encodeScriptMem(e.scriptMem);
      out.push({
        type: e.type, x: e.x, y: e.y, z: e.z, vx: e.vx, vy: e.vy, vz: e.vz,
        yaw: e.yaw, health: e.health, age: e.age, baby: e.baby, growTimer: e.growTimer,
        breedCooldown: e.breedCooldown, loveTimer: e.loveTimer, persistent: e.persistent,
        tamed: e.tamed, sitting: e.sitting, variant: e.variant, sheared: e.sheared,
        // Durable scratch (villager home/bed/jobsite/willingness, MB.7). Transient
        // path scratch rides along harmlessly (it's recomputed each tick).
        data: { ...e.data },
        ...(e.pack ? { pack: clonePack(e.pack).items } : {}),
        ...(scriptMem ? { scriptMem } : {}),
        // §27.13: absent unless set, so an entity with no override saves exactly as
        // it did at v3 before M47.
        ...(e.scriptOverride !== undefined ? { scriptOverride: e.scriptOverride } : {}),
        // §28.12: heredity state, absent unless present — a rule-off entity's row is
        // byte-identical to pre-M48.
        ...(e.genome ? { genome: encodeGenome(e.genome) } : {}),
        ...(e.parents ? { parents: [e.parents[0], e.parents[1]] as [number, number] } : {}),
        ...(e.name !== undefined ? { name: e.name } : {}),
        // The ID travels only with heredity state (M48.6, D-TF-243): parents and notebook
        // records name animals by id, and ids are otherwise re-issued on every load. An
        // animal with none of it saves exactly the row it always did.
        ...(e.genome || e.parents ? { id: e.id } : {}),
      });
    }
    return out;
  }

  loadJSON(list: readonly SavedEntity[] | undefined): void {
    if (!list) return;
    // M48.6 (D-TF-243): restore the ids that carry lineage. Every VALID saved id —
    // positive, unique within the save, not already live here — is reserved first, and
    // fresh ids start above them, so no id-less row can be handed an id a later row owns.
    const keep = new Set<number>();
    for (const s of list) {
      const id = s.id;
      if (Number.isInteger(id) && id! > 0 && !keep.has(id!) && !this.byId(id!)) keep.add(id!);
    }
    for (const id of keep) this.nextId = Math.max(this.nextId, id + 1);
    for (const s of list) {
      // §28.4: a saved genome is decoded against TODAY's table. A length that no longer
      // matches (a collection changed the genes) is E-51 — the animal re-rolls from
      // `her:roll` under the rule, with one warning per species per session, and never
      // crashes the load. Decoded regardless of the rule: "off leaves genomes in place".
      const genes = this.defs.get(s.type)?.genes;
      let genome: Uint8Array | undefined;
      if (genes && typeof s.genome === 'string') {
        genome = decodeGenome(s.genome, genes) ?? undefined;
        if (!genome && !this.warnedGenomes.has(s.type)) {
          this.warnedGenomes.add(s.type);
          this.warn?.(`a saved ${s.type} genome does not match its gene table — re-rolling (E-51)`);
        }
      }
      const parents = validParents(s.parents);
      const id = s.id !== undefined && keep.delete(s.id) ? s.id : undefined;
      const e = this.spawn(s.type, s.x, s.y, s.z, {
        baby: s.baby, persistent: s.persistent, variant: s.variant, tamed: s.tamed,
        ...(genome ? { genome } : {}), ...(parents ? { parents } : {}), ...(id !== undefined ? { id } : {}),
      });
      if (!e) continue;
      e.vx = s.vx; e.vy = s.vy; e.vz = s.vz;
      e.yaw = s.yaw; e.health = s.health; e.age = s.age;
      e.growTimer = s.growTimer; e.breedCooldown = s.breedCooldown; e.loveTimer = s.loveTimer;
      e.sitting = s.sitting ?? false; e.sheared = s.sheared ?? false;
      if (s.data) Object.assign(e.data, s.data);
      if (s.pack) e.pack = { items: s.pack.map((it) => (it ? cloneStack(it) : null)) };
      // ENG-11: a save written before §27.7 carries no `scriptMem` at all, so this
      // is `undefined` and the entity loads with empty memory and no error — the
      // obligation `tests/fixtures/save-pre-scriptmem.json` pins.
      const mem = decodeScriptMem(s.scriptMem);
      if (mem) e.scriptMem = mem;
      // §27.13: untrusted like the rest of the row (E-33) — a non-identifier is dropped,
      // and a pre-M47 row simply has none. Whether the name still resolves is decided at
      // dispatch, where a dangling one falls back rather than failing the load (E-50).
      const override = decodeScriptOverride(s.scriptOverride);
      if (override !== undefined) e.scriptOverride = override;
      // Rule off: `spawn` kept no genome, but a saved one stays with the animal (§28.2).
      if (genome && !e.genome) e.genome = genome;
      if (parents && !e.parents) e.parents = parents;
      const name = decodeEntityName(s.name);
      if (name !== undefined) e.name = name;
    }
  }
}

/** Persisted entity form (ARCH §3.6). Transient AI scratch is not saved. */
export interface SavedEntity {
  type: string;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  yaw: number;
  health: number;
  age: number;
  baby: boolean;
  growTimer: number;
  breedCooldown: number;
  loveTimer: number;
  persistent: boolean;
  /** Durable mob state (MOB-2, M13.1). Optional for backward-compatible loads. */
  tamed?: boolean;
  sitting?: boolean;
  variant?: number;
  sheared?: boolean;
  /** Durable scratch (villager home/bed/jobsite/willingness, MB.7). Optional. */
  data?: Record<string, number>;
  /** A chested pack animal's saddlebags (M19 remainder). Optional. */
  pack?: (ItemStack | null)[];
  /** Per-entity script memory (§27.7, SCR-8; save format v3). Absent on every
   *  pre-M45 save AND on every entity that never ran a `mem.set` — absent ⇒ empty
   *  memory, which is the whole ENG-11 migration. */
  scriptMem?: ScriptMem;
  /** Per-instance script override (§27.13, SCR-18; rides save format v3). Absent on
   *  every row without one — absent ⇒ the type's script. */
  scriptOverride?: string;
  /** Heredity (§28.12): base64 genome and parent ids. Absent on every pre-M48 row and on
   *  every animal that never lived under the rule. */
  genome?: string;
  parents?: [number, number];
  /** A name-tag name (§28.9; save v4). */
  name?: string;
  /** The entity's id, saved only with heredity state (M48.6, D-TF-243). */
  id?: number;
}

/** §28.8/§28.11's host-facing heredity events. `her:birth` fires only for a
 *  genetic child (a genome was inherited) — the notebook's auto-record trigger
 *  (M50.4): a host checks every player's notebook for both `parents` and
 *  upserts the child into whichever ones qualify. */
export type HeredityEvent =
  | { kind: 'her:oldAge'; id: number; x: number; y: number; z: number }
  | { kind: 'her:capacity'; player: number }
  | { kind: 'her:birth'; id: number; parents: [number, number] };

/** A saved `parents` pair, if it is one (E-33: a save is untrusted input). */
function validParents(raw: unknown): [number, number] | undefined {
  if (!Array.isArray(raw) || raw.length !== 2) return undefined;
  const [a, b] = raw as unknown[];
  return Number.isInteger(a) && Number.isInteger(b) ? [a as number, b as number] : undefined;
}

// ── Per-entity script memory (ARCH §27.7, SCR-8) ──────────────────────────────
// The STORE half of the `mem.get`/`mem.set` verb pair (§27.5): the two functions the
// entity host hands the script API as `memGet`/`memSet`. Every §27.7 cap is checked
// here, because the store is the only side that knows what the entity already holds.
//
// A refused write returns FALSE and never throws — the abort is the verb layer's to
// raise, so a cap can abort one dispatch (E-30) with no path by which it could throw
// into the tick loop. No check runs after a mutation either, so a refusal leaves the
// stored memory exactly as it was (E-43).

/** Keys the entity currently holds — 0 for a mob that never stored anything. */
export function scriptMemSize(e: Entity): number {
  return e.scriptMem ? Object.keys(e.scriptMem).length : 0;
}

/** `mem.get`'s store read: the stored value, or `undefined` when the entity has none
 *  (the verb layer then applies the script's required default). Own keys only, so an
 *  identifier that happens to name an `Object.prototype` member (`toString`,
 *  `constructor`) reads as unset rather than as a function. */
export function scriptMemGet(e: Entity, key: string): ScriptMemValue | undefined {
  const mem = e.scriptMem;
  if (!mem || !Object.prototype.hasOwnProperty.call(mem, key)) return undefined;
  return mem[key];
}

/**
 * `mem.set`'s store write. Returns false — refusing to store anything — when:
 *
 *  - `key` is not identifier-shaped;
 *  - `value` is not one of the language's primitives (a **vec3 and a list are both
 *    rejected**: memory is for state machines, not geometry — store three numbers),
 *    or is a non-finite number, or is a string over `SCR.MEM.STR_MAX`;
 *  - `key` is NEW and the entity already holds `SCR.MEM.KEYS` of them.
 *
 * Overwriting an existing key at the key cap always succeeds: the cap bounds the bag,
 * not the writes. `value` is `unknown` because this is the last gate before entity
 * state (and therefore a save) — it validates rather than trusts.
 */
export function scriptMemSet(e: Entity, key: string, value: unknown): boolean {
  if (!isScriptMemKey(key) || !isScriptMemValue(value)) return false;
  const mem = e.scriptMem;
  if (!mem) { e.scriptMem = { [key]: value }; return true; }
  const fresh = !Object.prototype.hasOwnProperty.call(mem, key);
  if (fresh && Object.keys(mem).length >= SCR.MEM.KEYS) return false;
  mem[key] = value;
  return true;
}

/** Ray vs AABB (slab method). Returns the entry distance, or null for a miss /
 *  a box behind the origin. Direction (dx,dy,dz) need not be normalized; the
 *  returned t is in direction-length units (unit dir ⇒ t is blocks). */
function rayAabb(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): number | null {
  let tmin = -Infinity, tmax = Infinity;
  const axes: [number, number, number, number][] = [
    [ox, dx, minX, maxX], [oy, dy, minY, maxY], [oz, dz, minZ, maxZ],
  ];
  for (const [o, d, lo, hi] of axes) {
    if (Math.abs(d) < 1e-8) { if (o < lo || o > hi) return null; continue; }
    let t1 = (lo - o) / d, t2 = (hi - o) / d;
    if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
    tmin = Math.max(tmin, t1);
    tmax = Math.min(tmax, t2);
    if (tmin > tmax) return null;
  }
  if (tmax < 0) return null;
  return tmin >= 0 ? tmin : 0;
}

export { halfOf, heightOf, scaleOf };
